// =============================================================================
// app/AuthGuard.tsx — Proteção de rotas autenticadas.
//
// Redireciona para /login se não autenticado.
// Preserva a rota pretendida em state para redirect pós-login (futuro).
//
// Primeiro acesso (F56-S02): usuário com senha temporária só pode usar /primeiro-acesso.
//   surface="app"          → pendente é levado a /primeiro-acesso
//   surface="first-access" → não pendente é levado a "/" (a própria tela)
// O estado vem do login ou de GET /api/auth/me (nunca de storage). Enquanto ele é
// desconhecido (reload), NADA do sistema renderiza — assim nenhuma tela dispara chamadas
// que o servidor recusaria com 403. Isto é cosmético: a autorização real é do servidor.
// =============================================================================

import * as React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { Button } from '../components/ui/Button';
import { decideGuard, FIRST_ACCESS_PATH, type GuardSurface } from '../features/auth/firstAccess';
import { useAuth } from '../features/auth/useAuth';
import { useResolveFirstAccess } from '../features/auth/useFirstAccess';
import { useAuthStore } from '../lib/auth-store';

import { useSessionBootstrap } from './SessionBootstrap';

interface AuthGuardProps {
  children: React.ReactNode;
  /** Qual "lado" do primeiro acesso esta rota protege. Padrão: as rotas do sistema. */
  surface?: Exclude<GuardSurface, 'public'>;
}

function Splash({ label }: { label: string }): React.JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="flex items-center justify-center min-h-screen bg-[var(--bg-elev-1)]"
    >
      <div className="flex flex-col items-center gap-3">
        <div
          className="w-8 h-8 rounded-full border-2 border-border-strong border-t-azul animate-spin"
          aria-hidden="true"
        />
        <span className="sr-only">{label}</span>
      </div>
    </div>
  );
}

/** Falha ao confirmar o estado da conta: não libera o sistema, oferece repetir ou sair. */
function ResolveError({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  const { logout } = useAuth();
  return (
    <div className="flex items-center justify-center min-h-screen bg-[var(--bg-elev-1)] px-6">
      <div
        role="alert"
        className="w-full max-w-[420px] rounded-lg border border-border bg-surface-1 p-6 shadow-e3 flex flex-col gap-4"
      >
        <p className="font-display font-bold text-ink text-xl">Não conseguimos abrir sua conta</p>
        <p className="text-sm text-ink-2">
          Houve uma falha ao confirmar o seu acesso. Confira a internet e tente de novo.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button variant="primary" className="sm:flex-1" onClick={onRetry}>
            Tentar de novo
          </Button>
          <Button variant="ghost" className="sm:flex-1" onClick={() => void logout()}>
            Sair
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Wrapper de rotas protegidas.
 * Uso: <Route element={<AuthGuard><AppLayout /></AuthGuard>}>
 *
 * Bootstrap-aware: enquanto o SessionBootstrap está restaurando a sessão
 * via refresh-cookie, mostra um splash discreto em vez de redirecionar
 * para /login (evita o flash de login no reload de usuários autenticados).
 */
export function AuthGuard({ children, surface = 'app' }: AuthGuardProps): React.JSX.Element {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const firstAccess = useAuthStore((state) => state.firstAccess);
  const { status } = useSessionBootstrap();
  const location = useLocation();

  const decision = decideGuard({
    surface,
    bootstrapping: status === 'bootstrapping',
    isAuthenticated,
    firstAccess,
  });

  // O hook roda sempre (regra dos hooks); só busca quando a decisão pede.
  const resolver = useResolveFirstAccess(decision.type === 'resolve');

  switch (decision.type) {
    case 'splash':
      return <Splash label="Restaurando sessão…" />;
    case 'resolve':
      if (resolver.isError) return <ResolveError onRetry={() => void resolver.refetch()} />;
      return <Splash label="Verificando seu acesso…" />;
    case 'login':
      // Guarda a rota pretendida para redirect pós-login (F1-S09+)
      return <Navigate to="/login" state={{ from: location }} replace />;
    case 'to-first-access':
      return <Navigate to={FIRST_ACCESS_PATH} replace />;
    case 'to-home':
      return <Navigate to="/" replace />;
    case 'render':
      return <>{children}</>;
  }
}
